import React, { useState, useEffect } from "react";
import { Search, X, LoaderCircle } from "lucide-react";
import SearchProductCard from "src/components/ui-elements/SearchProductCard";
import debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import { fetchAndSetSearchProducts } from "src/helpers/searchHelper";
import { fetchTrendingProducts } from "src/services/TrendingProductApi";
import { getImageUrl } from "src/utils/imageUrl";

const SearchDrawer = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTrendingLoading, setIsTrendingLoading] = useState(false);
  const [error, setError] = useState(null);
  const currency = useSelector((state) => state.currency.currencyName);

  // Fetch trending products when the drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchTrending();
    }
  }, [isOpen]);

  const fetchTrending = async () => {
    setIsTrendingLoading(true);
    try {
      const products = await fetchTrendingProducts();
      const processedProducts = products.map(item => ({
        ...item,
        image: item?.images && item.images.length > 0
          ? getImageUrl(item.images[0].imagePath)
          : item.image,
      }));
      setTrendingProducts(processedProducts);
    } catch (err) {
      console.error("Error fetching trending products:", err);
      setError("Failed to load trending products");
    } finally {
      setIsTrendingLoading(false);
    }
  };

  // Handle search query changes
  useEffect(() => {
    setIsLoading(true);
    const debouncedSearch = debounce(async (query) => {
      const validQuery = /^[a-zA-Z0-9\s]+$/;
      if (query.trim() === "" || !validQuery.test(query)) {
        setSearchResults([]);
        setError(null);
        setIsLoading(false);
        return;
      }
      await fetchAndSetSearchProducts(
        query,
        setSearchResults,
        setError,
        setIsLoading
      );
    }, 500);

    if (searchQuery.trim() !== "") {
      debouncedSearch(searchQuery);
    } else {
      setIsLoading(false);
    }
    return () => debouncedSearch.cancel();
  }, [searchQuery]);

  // Determine which products to display
  const displayProducts = searchQuery.trim() === "" 
    ? trendingProducts 
    : searchResults;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] px-2 bg-white shadow-lg z-30 transform font-outfit ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out`}
      >
        <div className="flex items-center justify-between px-4 py-4 sm:py-8 ">
          <h1 className="text-[20px] md:text-[24px] lg:text-2xl text-center font-outfitSemiBold">
            Search
          </h1>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
          >
            <X size={24} />
          </button>
        </div>

        <div
          className="overflow-auto h-full
          [&::-webkit-scrollbar]:w-1
          [&::-webkit-scrollbar-track]:bg-white
          [&::-webkit-scrollbar-thumb]:bg-gray-400 pb-20"
        >
          {/* Search Box */}
          <div className="px-4 py-4 relative border-b">
            <div className="relative flex items-center">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                }}
                className="w-full pl-12 pr-12 py-4 border rounded-full border-1 bg-gray-100 text-black font-outfitRegular placeholder:text-[#707070]"
              />
            </div>
          </div>

          <div className="p-4">
            <h1 className="text-lg font-outfitMedium">
              {searchQuery.trim() === ""
                ? "Most Trending Products"
                : "Search Results "}
              {searchQuery.trim() !== "" && <>for {searchQuery}</>}
            </h1>
          </div>

          {/* Product List */}
          <div className="pb-2 space-y-4 overflow-auto">
            {error ? (
              <div className="text-center text-red-500">{error}</div>
            ) : displayProducts.length > 0 ? (
              displayProducts.map((product, index) => (
                <SearchProductCard
                  key={product.id || index}
                  image={product.image}
                  name={product.name}
                  price={product.price}
                  slug={product.slug}
                  currency={currency}
                  onClose={onClose}
                />
              ))
            ) : (
              <>
                {isLoading || isTrendingLoading ? (
                  <>
                    <div className="flex justify-center items-center">
                      <LoaderCircle
                        size={24}
                        className="animate-spin text-black-500"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <p className="text-center text-gray-500">
                        {searchQuery.trim() === ""
                          ? "No trending products available"
                          : `No products found with the name "${searchQuery}"`}
                        <br />
                        {searchQuery.trim() !== "" &&
                          "Check the spelling or use a different word or phrase."}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

        <div className="mt-5"></div>

          </div>
        </div>
      </div>
    </>
  );
};

export default SearchDrawer;